// src/api/helpers.ts
import { api, API_BASE_URL } from './client';

// Format Naira currency
export const formatNaira = (amount: number): string => {
  return `₦${amount.toLocaleString('en-NG', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

// Calculate platform fee (10%)
export const calculatePlatformFee = (amount: number): number => {
  return Math.round(amount * 0.10 * 100) / 100;
};

// Calculate rider payout (90%)
export const calculateRiderPayout = (amount: number): number => {
  return Math.round(amount * 0.90 * 100) / 100;
};

// Get payment breakdown
export const getPaymentBreakdown = (amount: number) => {
  const platformFee = calculatePlatformFee(amount);
  const riderPayout = calculateRiderPayout(amount);
  
  return {
    total: amount,
    platformFee,
    riderPayout,
    platformFeePercentage: '10%',
    riderPayoutPercentage: '90%'
  };
};

// Check if user can access a feature based on role
export const canAccess = (userRole: string, requiredRoles: string[]): boolean => {
  return requiredRoles.includes(userRole);
};

// Get user dashboard URL based on role
export const getDashboardUrl = (role: string): string => {
  switch (role) {
    case 'customer':
      return '/dashboard/customer';
    case 'rider':
      return '/dashboard/rider';
    case 'admin':
      return '/admin';
    case 'support':
      return '/support';
    default:
      return '/';
  }
};

// Handle API error
export const handleApiError = (error: any): string => {
  if (error.response) {
    const status = error.response.status;
    const message = error.response.data?.message || error.response.data?.error;
    
    switch (status) {
      case 400:
        return message || 'Bad request';
      case 401:
        return 'Session expired. Please login again.';
      case 403:
        return 'You do not have permission to perform this action';
      case 404:
        return 'Resource not found';
      case 422:
        return message || 'Validation error';
      case 429:
        return 'Too many requests. Please try again later';
      case 500:
        return 'Server error. Please try again later';
      default:
        return message || `Error ${status}`;
    }
  } else if (error.request) {
    return 'Network error. Please check your connection';
  } else {
    return error.message || 'An unexpected error occurred';
  }
};

// Get API URL
export const getApiUrl = (path: string): string => {
  return `${API_BASE_URL}${path}`;
};

// Ensure string ID for API calls
export const ensureStringId = (id: any): string => {
  if (!id) return '';
  if (typeof id === 'string') return id;
  if (typeof id === 'object') {
    if (id._id) return typeof id._id === 'string' ? id._id : id._id.toString();
    if (id.id) return typeof id.id === 'string' ? id.id : id.id.toString();
    if (id.toString && typeof id.toString === 'function') return id.toString();
  }
  return String(id);
};

// Socket.IO helper functions
export const socketHelpers = {
  connect: (token: string) => {
    console.log('Connecting to socket with token');
  },
  
  disconnect: () => {
    console.log('Disconnecting from socket');
  },
  
  joinOrderRoom: (orderId: string) => {
    console.log(`Joining order room: ${orderId}`);
  },
  
  leaveOrderRoom: (orderId: string) => {
    console.log(`Leaving order room: ${orderId}`);
  },
  
  sendMessage: (orderId: string, message: string) => {
    console.log(`Sending message to order ${orderId}`);
  },
  
  sendTyping: (orderId: string, isTyping: boolean) => {
    console.log(`Typing status for order ${orderId}: ${isTyping}`);
  },
  
  markAsRead: (orderId: string, messageIds?: string[]) => {
    console.log(`Marking messages as read for order ${orderId}`);
  },
  
  inviteSupport: (orderId: string, data: any) => {
    console.log(`Inviting support for order ${orderId}`);
  }
};
